import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import './AdminLayout.css';
import './AdminAds.css';

const PAGE_SIZE = 50;

export default function AdminAuditLogs() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const isArabic = lang === 'ar';

  const [filters, setFilters] = useState({ action: '', target_type: '', target_id: '', actor_admin_id: '' });
  const [draft, setDraft] = useState({ action: '', target_type: '', target_id: '', actor_admin_id: '' });
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const r = await fetch(
        `${getApiBase()}/api/admin/dashboard/audit-logs?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!r.ok) {
        toast.error(isArabic ? 'فشل تحميل السجل' : 'Failed to load audit log');
        return;
      }
      const j = await r.json();
      setData(j);
    } finally {
      setLoading(false);
    }
  }, [token, filters, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const apply = () => { setFilters({ ...draft }); setSkip(0); };
  const clearAll = () => { setDraft({ action: '', target_type: '', target_id: '', actor_admin_id: '' }); setFilters({ action: '', target_type: '', target_id: '', actor_admin_id: '' }); setSkip(0); };
  const pages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div data-testid="admin-audit-logs">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-audit-logs-title">
            {isArabic ? 'سجل المراجعة العام' : 'Global Audit Log'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'كل إجراء يقوم به المشرفون والمسؤولون.'
              : 'Every action performed by admins and moderators.'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-audit-logs-refresh">
          <RefreshCw size={14} />{isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-card" style={{ marginBottom: 14 }}>
        <div className="admin-audit-filters">
          <input
            type="text"
            placeholder={isArabic ? 'الإجراء (مثل ad.approve)' : 'Action (e.g. ad.approve)'}
            value={draft.action}
            onChange={(e) => setDraft({ ...draft, action: e.target.value })}
            data-testid="audit-filter-action"
          />
          <input
            type="text"
            placeholder={isArabic ? 'نوع الهدف (ad, user, …)' : 'Target type (ad, user, …)'}
            value={draft.target_type}
            onChange={(e) => setDraft({ ...draft, target_type: e.target.value })}
            data-testid="audit-filter-target-type"
          />
          <input
            type="text"
            placeholder={isArabic ? 'معرّف الهدف' : 'Target id'}
            value={draft.target_id}
            onChange={(e) => setDraft({ ...draft, target_id: e.target.value })}
            data-testid="audit-filter-target-id"
          />
          <input
            type="text"
            placeholder={isArabic ? 'معرّف المشرف' : 'Actor admin id'}
            value={draft.actor_admin_id}
            onChange={(e) => setDraft({ ...draft, actor_admin_id: e.target.value })}
            data-testid="audit-filter-actor"
          />
          <button type="button" className="admin-btn primary" onClick={apply} data-testid="audit-filter-apply">
            {isArabic ? 'تطبيق' : 'Apply'}
          </button>
          <button type="button" className="admin-btn ghost" onClick={clearAll} data-testid="audit-filter-clear">
            {isArabic ? 'مسح' : 'Clear'}
          </button>
        </div>
      </div>

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-audit-logs-table">
            <thead>
              <tr>
                <th>{isArabic ? 'الوقت' : 'Time'}</th>
                <th>{isArabic ? 'الإجراء' : 'Action'}</th>
                <th>{isArabic ? 'الهدف' : 'Target'}</th>
                <th>{isArabic ? 'الفاعل' : 'Actor'}</th>
                <th>{isArabic ? 'تغيّر' : 'Change'}</th>
                <th>{isArabic ? 'السبب' : 'Reason'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={6} style={{ padding: 24, textAlign: 'center' }}><Loader2 className="animate-spin" size={16} /></td></tr>)}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-audit-logs-empty">
                  {isArabic ? 'لا توجد إجراءات بعد.' : 'No admin actions yet.'}
                </td></tr>
              )}
              {!loading && data.items.map((it, idx) => (
                <tr key={idx} data-testid={`audit-log-row-${idx}`}>
                  <td className="admin-ads-date">{it.timestamp ? new Date(it.timestamp).toLocaleString() : ''}</td>
                  <td><span className="admin-pill blue">{it.action}</span></td>
                  <td className="admin-ads-ad-id">{it.target_type}:{it.target_id || '—'}</td>
                  <td>
                    <div className="admin-ads-owner">
                      <div>{it.actor_admin_id || '—'}</div>
                      <div className="admin-ads-owner-email">{it.actor_role || ''}</div>
                    </div>
                  </td>
                  <td>{(it.old_value?.status || '—')} → {(it.new_value?.status || '—')}</td>
                  <td style={{ color: 'var(--admin-text-dim)' }}>{it.reason || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-audit-logs-total">{isArabic ? `الإجمالي: ${data.total}` : `Total: ${data.total}`}</span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="audit-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label">{currentPage} / {pages}</span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="audit-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
